import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import TimerBar from "./time_tracker/TimerBar";
import TimeEntries from "./time_tracker/TimeEntries";
import ProjectDistribution from "./time_tracker/ProjectDistribution";
import CalendarView from "./time_tracker/CalendarView";
import DateRangeSelect from "./common/DateRangeSelect";

const FONT = "Segoe UI, sans-serif, system-ui";
const RANGES = ["today", "week", "month", "custom"];
const TIME_ENTRY_CHANGED_EVENT = "time-entry:changed";

export default function TimeTrackerPage() {
  const currentUser = useSelector((s) => s.current_internal_user);
  const [range, setRange] = useState("today");
  const [customRange, setCustomRange] = useState({ startDate: null, endDate: null });
  const [refreshKey, setRefreshKey] = useState(0);

  const triggerRefresh = () => setRefreshKey((n) => n + 1);

  useEffect(() => {
    const handler = () => triggerRefresh();
    window.addEventListener(TIME_ENTRY_CHANGED_EVENT, handler);
    return () => window.removeEventListener(TIME_ENTRY_CHANGED_EVENT, handler);
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", width: "100%", backgroundColor: "hsl(140 10% 95%)", fontFamily: FONT }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 4, maxWidth: 1280, mx: "auto", width: "100%" }}>
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography sx={{ fontFamily: "Segoe UI Bold, system-ui, sans-serif", fontSize: 24, color: "hsl(150 30% 12%)", lineHeight: 1.2, letterSpacing: -0.2 }}>
              Time tracker
            </Typography>
            <Typography sx={{ fontFamily: "Segoe UI Regular, system-ui, sans-serif", fontSize: 14, color: "hsl(150 10% 46%)", mt: 0.5 }}>
              {currentUser?.first_name ? `Hey ${currentUser.first_name} — ` : ""}track what you're working on and where the day went.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {range === "custom" && (
              <DateRangeSelect
                startDate={customRange.startDate}
                endDate={customRange.endDate}
                onChange={(r) => setCustomRange(r)}
              />
            )}
            <Box sx={{ display: "flex", backgroundColor: "hsl(140 6% 90%)", borderRadius: "8px", p: "2px", border: "1px solid hsl(140 8% 88%)" }}>
              {RANGES.map((r) => (
                <Box
                  key={r}
                  component="button"
                  onClick={() => setRange(r)}
                  sx={{
                    all: "unset",
                    px: 2, py: 0.75,
                    fontFamily: range === r ? "Segoe UI Semibold, system-ui, sans-serif" : "Segoe UI Regular, system-ui, sans-serif",
                    fontSize: 12,
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 150ms ease",
                    color: range === r ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
                    backgroundColor: range === r ? "#ffffff" : "transparent",
                    boxShadow: range === r ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                    "&:hover": { color: "hsl(150 30% 12%)" },
                  }}
                >
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <TimerBar onChange={triggerRefresh} />

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 7fr) minmax(0, 5fr)" }, gap: 3 }}>
          <TimeEntries
            range={range}
            startDate={customRange.startDate}
            endDate={customRange.endDate}
            refreshKey={refreshKey}
          />
          <ProjectDistribution
            range={range}
            startDate={customRange.startDate}
            endDate={customRange.endDate}
            refreshKey={refreshKey}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <CalendarView refreshKey={refreshKey} />
        </Box>
      </Box>
    </Box>
  );
}
